import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const BLOCKED_SHORTCUT_KEYS = new Set(['a', 'c', 'v', 'x']);
const SCREENSHOT_SHORTCUT_KEYS = new Set(['3', '4', '5']);
const TEMPORARY_OVERLAY_MS = 1500;

const isNodeWithin = (container, node) => {
    if (!container || !node) return false;
    return node === container || container.contains(node);
};

const selectionIntersectsContainer = (container) => {
    const selection = window.getSelection?.();
    if (!container || !selection || selection.rangeCount === 0 || selection.isCollapsed) return false;

    for (let i = 0; i < selection.rangeCount; i += 1) {
        const range = selection.getRangeAt(i);
        if (isNodeWithin(container, range.commonAncestorContainer)) return true;

        try {
            if (range.intersectsNode(container)) return true;
        } catch (error) {
            return false;
        }
    }

    return false;
};

const clearProtectedSelection = (container) => {
    if (!selectionIntersectsContainer(container)) return;
    window.getSelection?.().removeAllRanges();
};

const isProtectedInteraction = (container, event, lastPointerStartedInside) => {
    return (
        isNodeWithin(container, event.target) ||
        isNodeWithin(container, document.activeElement) ||
        selectionIntersectsContainer(container) ||
        lastPointerStartedInside.current
    );
};

const isScreenshotShortcut = (event) => {
    const key = String(event.key || '').toLowerCase();
    const code = String(event.code || '');

    if (event.key === 'PrintScreen' || event.code === 'PrintScreen') return true;
    return (
        event.metaKey &&
        event.shiftKey &&
        (SCREENSHOT_SHORTCUT_KEYS.has(key) || ['Digit3', 'Digit4', 'Digit5'].includes(code))
    );
};

const HandoutContentProtection = ({ children, sx, ...props }) => {
    const contentRef = useRef(null);
    const lastPointerStartedInside = useRef(false);
    const overlayTimeoutRef = useRef(null);
    const [showPrivacyOverlay, setShowPrivacyOverlay] = useState(false);

    useEffect(() => {
        const showTemporaryOverlay = () => {
            window.clearTimeout(overlayTimeoutRef.current);
            setShowPrivacyOverlay(true);
            overlayTimeoutRef.current = window.setTimeout(() => {
                if (document.visibilityState === 'visible') {
                    setShowPrivacyOverlay(false);
                }
            }, TEMPORARY_OVERLAY_MS);
        };

        const blockProtectedEvent = (event) => {
            const container = contentRef.current;
            if (!container || !isProtectedInteraction(container, event, lastPointerStartedInside)) return;

            event.preventDefault();
            event.stopPropagation();
            clearProtectedSelection(container);
        };

        const handlePointerStart = (event) => {
            lastPointerStartedInside.current = isNodeWithin(contentRef.current, event.target);
        };

        const handleKeyDown = (event) => {
            const container = contentRef.current;
            if (!container) return;

            if (isScreenshotShortcut(event)) {
                event.preventDefault();
                showTemporaryOverlay();
                return;
            }

            const key = String(event.key || '').toLowerCase();
            if (
                (event.metaKey || event.ctrlKey) &&
                BLOCKED_SHORTCUT_KEYS.has(key) &&
                isProtectedInteraction(container, event, lastPointerStartedInside)
            ) {
                blockProtectedEvent(event);
            }
        };

        const handleVisibilityChange = () => {
            setShowPrivacyOverlay(document.visibilityState === 'hidden');
        };

        const blockedEvents = ['copy', 'cut', 'paste', 'selectstart', 'dragstart', 'contextmenu'];
        blockedEvents.forEach((eventName) => document.addEventListener(eventName, blockProtectedEvent, true));
        document.addEventListener('mousedown', handlePointerStart, true);
        document.addEventListener('touchstart', handlePointerStart, true);
        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.clearTimeout(overlayTimeoutRef.current);
            blockedEvents.forEach((eventName) => document.removeEventListener(eventName, blockProtectedEvent, true));
            document.removeEventListener('mousedown', handlePointerStart, true);
            document.removeEventListener('touchstart', handlePointerStart, true);
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    return (
        <Box
            ref={contentRef}
            data-testid="handout-protected-content"
            {...props}
            sx={{
                position: 'relative',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                ...sx,
            }}
        >
            {children}
            {showPrivacyOverlay && (
                <Box
                    aria-hidden="true"
                    data-testid="handout-privacy-overlay"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 2147483647,
                        backgroundColor: '#8A8A8A',
                        pointerEvents: 'auto',
                    }}
                />
            )}
        </Box>
    );
};

export default HandoutContentProtection;
